import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/20/solid";
import {
    ActivityRowIconKind,
    activityRowIcons,
} from "components/details-page-v-next/activity-rows";
import { InteractiveTextLink } from "components/ui/interactive-text-link";
import React from "react";
import { Link } from "react-router-dom";
import {
    ActivityColumn,
    LeaderboardRow,
    LeaderboardSort,
    nameSortKey,
    totalSortKey,
} from "./activities-report";

export const ActivityColumnIcon: React.FC<{
    iconKind: ActivityRowIconKind | null;
    className: string;
}> = ({ iconKind, className }) => {
    if (!iconKind) {
        return null;
    }
    const Icon = activityRowIcons[iconKind];
    return <Icon aria-hidden="true" className={className} />;
};

const nextSort = (current: LeaderboardSort, key: string): LeaderboardSort =>
    current.key === key
        ? { key, direction: current.direction === "asc" ? "desc" : "asc" }
        : { key, direction: key === nameSortKey ? "asc" : "desc" };

interface SortableHeaderProps {
    sortKey: string;
    label: string;
    hint: string | null;
    icon: React.ReactNode;
    align: "left" | "right";
    sort: LeaderboardSort;
    onSort: (sort: LeaderboardSort) => void;
}

const SortableHeader: React.FC<SortableHeaderProps> = props => {
    const active = props.sort.key === props.sortKey;
    const DirectionIcon =
        props.sort.direction === "asc" ? ChevronUpIcon : ChevronDownIcon;
    return (
        <th
            scope="col"
            title={props.hint ?? undefined}
            aria-sort={
                active
                    ? props.sort.direction === "asc"
                        ? "ascending"
                        : "descending"
                    : "none"
            }
            className={`whitespace-nowrap px-4 py-3 ${
                props.align === "right" ? "text-right" : "text-left"
            }`}>
            <InteractiveTextLink
                className={`inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wide ${
                    active ? "text-gray-900" : ""
                }`}
                onClick={() => props.onSort(nextSort(props.sort, props.sortKey))}>
                {props.icon}
                <span>{props.label}</span>
                {active ? <DirectionIcon aria-hidden="true" className="size-3" /> : null}
            </InteractiveTextLink>
        </th>
    );
};

interface ActivitiesLeaderboardProps {
    rows: LeaderboardRow[];
    columns: ActivityColumn[];
    sort: LeaderboardSort;
    onSort: (sort: LeaderboardSort) => void;
    staffHref: (row: LeaderboardRow) => string;
}

const CountCell: React.FC<{ count: number; emphasis: "normal" | "total" }> = ({
    count,
    emphasis,
}) => (
    <td className="whitespace-nowrap px-4 py-3 text-right">
        <span
            className={`text-sm tabular-nums ${
                count === 0
                    ? "text-gray-400"
                    : emphasis === "total"
                      ? "font-semibold text-gray-900"
                      : "font-medium text-gray-900"
            }`}>
            {count}
        </span>
    </td>
);

export const ActivitiesLeaderboard: React.FC<ActivitiesLeaderboardProps> = ({
    rows,
    columns,
    sort,
    onSort,
    staffHref,
}) => (
    <div className="overflow-x-auto rounded-lg bg-white ring-1 ring-gray-200">
        <table className="min-w-full">
            <thead>
                <tr className="border-b border-gray-200 bg-gray-50 text-gray-500">
                    <SortableHeader
                        sortKey={nameSortKey}
                        label="Staff member"
                        hint={null}
                        icon={null}
                        align="left"
                        sort={sort}
                        onSort={onSort}
                    />
                    {columns.map(column => (
                        <SortableHeader
                            key={column.key}
                            sortKey={column.key}
                            label={column.label}
                            hint={column.hint}
                            icon={
                                <ActivityColumnIcon
                                    iconKind={column.iconKind}
                                    className="h-3.5 w-3.5 flex-shrink-0 text-gray-400"
                                />
                            }
                            align="right"
                            sort={sort}
                            onSort={onSort}
                        />
                    ))}
                    <SortableHeader
                        sortKey={totalSortKey}
                        label="Total"
                        hint={null}
                        icon={null}
                        align="right"
                        sort={sort}
                        onSort={onSort}
                    />
                </tr>
            </thead>
            <tbody>
                {rows.map(row => (
                    <tr
                        key={row.userId}
                        className="relative border-b border-gray-100 hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-900">
                            <Link
                                to={staffHref(row)}
                                className="after:absolute after:inset-0 hover:underline">
                                {row.name}
                            </Link>
                        </td>
                        {row.counts.map(cell => (
                            <CountCell
                                key={cell.columnKey}
                                count={cell.count}
                                emphasis="normal"
                            />
                        ))}
                        <CountCell count={row.total} emphasis="total" />
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);
